import React, { useState, useEffect } from 'react';
import { toast } from "react-toastify";
import { useUser } from "../../Context/UserContext";
import { FaDollarSign, FaCalendarAlt, FaRedo, FaArrowUp, FaArrowDown, FaMinus, FaSave } from "react-icons/fa";
import { Link } from 'react-router-dom';

const SortingPreferences = () => {
  const user = useUser();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  
  const [preferences, setPreferences] = useState({
    price_sort: {
      min_value: null,
      max_value: null,
      sort_order: 'none'
    },
    year_sort: {
      min_value: null,
      max_value: null,
      sort_order: 'none'
    }
  });

  const [tempPreferences, setTempPreferences] = useState({
    price_sort: {
      min_value: null,
      max_value: null,
      sort_order: 'none'
    },
    year_sort: {
      min_value: null,
      max_value: null,
      sort_order: 'none'
    }
  });

  // Fetch current preferences on component mount
  useEffect(() => {
    fetchPreferences();
  }, []);

  const fetchPreferences = async () => {
    if (!user?.email) return;
    
    setLoading(true);
    try {
      const response = await fetch(`https://app.akiya2.com/favorite-sorting?email=${user.email}`);
      const data = await response.json();
      
      if (data.success) {
        setPreferences(data.favorite_sorting);
        setTempPreferences(data.favorite_sorting);
        toast.success('Preferences loaded successfully');
      } else {
        toast.error('Failed to load preferences');
      }
    } catch (error) {
      console.error('Error fetching preferences:', error);
      toast.error('Error loading preferences');
    } finally {
      setLoading(false);
    }
  };

  const savePreferences = async () => {
    if (!user?.email) return;
    
    setSaving(true);
    try {
      const response = await fetch(`https://app.akiya2.com/favorite-sorting?email=${user.email}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(tempPreferences)
      });
      
      const data = await response.json();
      
      if (data.success) {
        setPreferences(data.favorite_sorting);
        setTempPreferences(data.favorite_sorting);
        setHasChanges(false);
        toast.success('Preferences saved successfully! 🎉');
      } else {
        toast.error('Failed to save preferences');
      }
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast.error('Error saving preferences');
    } finally {
      setSaving(false);
    }
  };

  const resetPreferences = async () => {
    if (!user?.email) return;
    
    setSaving(true);
    try {
      const response = await fetch(`https://app.akiya2.com/favorite-sorting?email=${user.email}`, {
        method: 'DELETE',
      });
      
      const data = await response.json();
      
      if (data.success) {
        setPreferences(data.favorite_sorting);
        setTempPreferences(data.favorite_sorting);
        setHasChanges(false);
        toast.success('Preferences reset to defaults');
      } else {
        toast.error('Failed to reset preferences');
      }
    } catch (error) {
      console.error('Error resetting preferences:', error);
      toast.error('Error resetting preferences');
    } finally {
      setSaving(false);
    }
  };

  const getSortIcon = (sortOrder) => {
    switch (sortOrder) {
      case 'ascending':
        return <FaArrowUp className="w-4 h-4 text-teal-600" />;
      case 'descending':
        return <FaArrowDown className="w-4 h-4 text-teal-600" />;
      default:
        return <FaMinus className="w-4 h-4 text-gray-400" />;
    }
  };

  const handlePriceChange = (field, value) => {
    let validatedValue = value;
    
    if (field === 'min_value') {
      // Min price: minimum 0
      if (validatedValue !== null && validatedValue < 0) {
        validatedValue = 0;
        toast.warning('Minimum price cannot be less than 0 yen');
      }
      // If max exists, ensure min doesn't exceed max
      if (validatedValue !== null && tempPreferences.price_sort.max_value !== null && validatedValue > tempPreferences.price_sort.max_value) {
        validatedValue = tempPreferences.price_sort.max_value;
        toast.warning('Minimum price cannot exceed maximum price');
      }
    }
    
    if (field === 'max_value') {
      // Max price: minimum 0
      if (validatedValue !== null && validatedValue < 0) {
        validatedValue = 0;
        toast.warning('Maximum price cannot be less than 0 yen');
      }
      // If min exists, ensure max doesn't go below min
      if (validatedValue !== null && tempPreferences.price_sort.min_value !== null && validatedValue < tempPreferences.price_sort.min_value) {
        validatedValue = tempPreferences.price_sort.min_value;
        toast.warning('Maximum price cannot be less than minimum price');
      }
    }
    
    const updatedPrice = { ...tempPreferences.price_sort, [field]: validatedValue };
    setTempPreferences({ ...tempPreferences, price_sort: updatedPrice });
    setHasChanges(true);
  };

  const handleYearChange = (field, value) => {
    let validatedValue = value;
    const currentYear = new Date().getFullYear();
    
    if (field === 'min_value') {
      // Min year: 1800-current year
      if (validatedValue !== null && validatedValue < 1800) {
        validatedValue = 1800;
        toast.warning('Minimum year cannot be less than 1800');
      }
      if (validatedValue !== null && validatedValue > currentYear) {
        validatedValue = currentYear;
        toast.warning(`Minimum year cannot exceed ${currentYear}`);
      }
      // If max exists, ensure min doesn't exceed max
      if (validatedValue !== null && tempPreferences.year_sort.max_value !== null && validatedValue > tempPreferences.year_sort.max_value) {
        validatedValue = tempPreferences.year_sort.max_value;
        toast.warning('Minimum year cannot exceed maximum year');
      }
    }
    
    if (field === 'max_value') {
      // Max year: 1800-current year
      if (validatedValue !== null && validatedValue < 1800) {
        validatedValue = 1800;
        toast.warning('Maximum year cannot be less than 1800');
      }
      if (validatedValue !== null && validatedValue > currentYear) {
        validatedValue = currentYear;
        toast.warning(`Maximum year cannot exceed ${currentYear}`);
      }
      // If min exists, ensure max doesn't go below min
      if (validatedValue !== null && tempPreferences.year_sort.min_value !== null && validatedValue < tempPreferences.year_sort.min_value) {
        validatedValue = tempPreferences.year_sort.min_value;
        toast.warning('Maximum year cannot be less than minimum year');
      }
    }
    
    const updatedYear = { ...tempPreferences.year_sort, [field]: validatedValue };
    setTempPreferences({ ...tempPreferences, year_sort: updatedYear });
    setHasChanges(true);
  };

  const handleAreaChange = (field, value) => {
    // Ensure area_sort exists
    const currentAreaSort = tempPreferences.area_sort || { min_value: null, max_value: null, sort_order: 'none' };
    
    let validatedValue = value;
    
    if (field === 'min_value') {
      // Min area: 0-3000
      if (validatedValue !== null && validatedValue < 0) {
        validatedValue = 0;
        toast.warning('Minimum area cannot be less than 0 m²');
      }
      if (validatedValue !== null && validatedValue > 3000) {
        validatedValue = 3000;
        toast.warning('Minimum area cannot exceed 3000 m²');
      }
      // If max exists, ensure min doesn't exceed max
      if (validatedValue !== null && currentAreaSort.max_value !== null && validatedValue > currentAreaSort.max_value) {
        validatedValue = currentAreaSort.max_value;
        toast.warning('Minimum area cannot exceed maximum area');
      }
    }
    
    if (field === 'max_value') {
      // Max area: 0-3000
      if (validatedValue !== null && validatedValue < 0) {
        validatedValue = 0;
        toast.warning('Maximum area cannot be less than 0 m²');
      }
      if (validatedValue !== null && validatedValue > 3000) {
        validatedValue = 3000;
        toast.warning('Maximum area cannot exceed 3000 m²');
      }
      // If min exists, ensure max doesn't go below min
      if (validatedValue !== null && currentAreaSort.min_value !== null && validatedValue < currentAreaSort.min_value) {
        validatedValue = currentAreaSort.min_value;
        toast.warning('Maximum area cannot be less than minimum area');
      }
    }
    
    const updatedArea = { ...currentAreaSort, [field]: validatedValue };
    setTempPreferences({ ...tempPreferences, area_sort: updatedArea });
    setHasChanges(true);
  };

  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = 1800; year <= currentYear; year++) {
      years.push(year);
    }
    return years;
  };

  // Reusable Save Button Component
  const SaveButton = ({ position = "top", className = "" }) => (
    <button
      onClick={savePreferences}
      disabled={saving || !hasChanges}
      className={`flex items-center gap-2 px-6 py-3 rounded-lg transition-all duration-200 ${
        hasChanges 
          ? 'bg-teal-600 text-white hover:bg-teal-700 hover:shadow-lg transform hover:scale-105' 
          : 'bg-gray-300 text-gray-500 cursor-not-allowed'
      } disabled:opacity-50 disabled:transform-none ${className}`}
    >
      {saving ? (
        <>
          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
          Saving...
        </>
      ) : (
        <>
          <FaSave className="w-4 h-4" />
          Save Changes
        </>
      )}
    </button>
  );

  // Reusable Reset Button Component
  const ResetButton = ({ className = "" }) => (
    <button
      onClick={resetPreferences}
      disabled={saving}
      className={`flex items-center gap-2 px-4 py-3 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 ${className}`}
    >
      <FaRedo className="w-4 h-4" />
      Reset
    </button>
  );

  if (loading) {
    return (
      <div className="flex-grow w-full p-6 bg-white border border-gray-200 rounded-lg shadow-md">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/2 mb-6"></div>
          <div className="space-y-4">
            <div className="h-32 bg-gray-200 rounded"></div>
            <div className="h-32 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-grow w-full p-6 bg-white border border-gray-200 rounded-lg shadow-md relative">
      {/* Custom Slider Styles */}
      <style>{`
        select {
          scrollbar-width: thin;
          scrollbar-color: #0d9488 #f1f5f9;
        }
        
        select::-webkit-scrollbar {
          width: 8px;
        }
        
        select::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 4px;
        }
        
        select::-webkit-scrollbar-thumb {
          background: #0d9488;
          border-radius: 4px;
        }
        
        select::-webkit-scrollbar-thumb:hover {
          background: #0f766e;
        }

        select option {
          padding: 8px 12px;
        }
      `}</style>

      {/* Header with Save Button */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-800">Alert Listing Email Sorting Preferences</h2>
          <p className="text-gray-500">Customize your property search filters to receive emails based on your preferences</p>
        </div>
        {/* <div className="flex gap-2">
          <SaveButton position="top" />
          <ResetButton />
        </div> */}
      </div>

      {/* Unsaved Changes Alert Bar */}
      {hasChanges && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
            <span className="text-yellow-800 text-sm font-medium">You have unsaved changes</span>
          </div>
          <SaveButton position="alert" className="px-4 py-2 text-sm" />
        </div>
      )}

      <hr className="my-4 border-gray-300" />

      <div className="space-y-6">
        {/* Price Sorting Section */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center gap-3 mb-4">
            <FaDollarSign className="w-5 h-5 text-teal-600" />
            <h3 className="text-lg font-semibold text-gray-800">Price Filtering</h3>
            {getSortIcon(tempPreferences.price_sort.sort_order)}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Min Price */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Minimum Price (¥)
              </label>
              <input
                type="number"
                value={tempPreferences.price_sort.min_value || ''}
                onChange={(e) => handlePriceChange('min_value', e.target.value ? parseInt(e.target.value) : null)}
                placeholder="No minimum"
                min="0"
                className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
                disabled={saving}
              />
            </div>

            {/* Max Price */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Maximum Price (¥)
              </label>
              <input
                type="number"
                value={tempPreferences.price_sort.max_value || ''}
                onChange={(e) => handlePriceChange('max_value', e.target.value ? parseInt(e.target.value) : null)}
                placeholder="No maximum"
                min="0"
                className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
                disabled={saving}
              />
            </div>

            {/* Sort Order */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sort Order
              </label>
              <select
                value={tempPreferences.price_sort.sort_order}
                onChange={(e) => handlePriceChange('sort_order', e.target.value)}
                className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
                disabled={saving}
              >
                <option value="none">No sorting</option>
                <option value="ascending">Low to High</option>
                <option value="descending">High to Low</option>
              </select>
            </div>
          </div>
        </div>

        {/* Year Sorting Section */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center gap-3 mb-4">
            <FaCalendarAlt className="w-5 h-5 text-teal-600" />
            <h3 className="text-lg font-semibold text-gray-800">Year Filtering</h3>
            {getSortIcon(tempPreferences.year_sort.sort_order)}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Min Year Picker */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Minimum Year
              </label>
              <div className="relative">
                <select
                  value={tempPreferences.year_sort.min_value || ''}
                  onChange={(e) => handleYearChange('min_value', e.target.value ? parseInt(e.target.value) : null)}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors appearance-none bg-white"
                  disabled={saving}
                  style={{ maxHeight: '200px' }}
                >
                  <option value="">No minimum</option>
                  {generateYearOptions().map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                  </svg>
                </div>
              </div>
            </div>

            {/* Max Year Picker */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Maximum Year
              </label>
              <div className="relative">
                <select
                  value={tempPreferences.year_sort.max_value || ''}
                  onChange={(e) => handleYearChange('max_value', e.target.value ? parseInt(e.target.value) : null)}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors appearance-none bg-white"
                  disabled={saving}
                  style={{ maxHeight: '200px' }}
                >
                  <option value="">No maximum</option>
                  {generateYearOptions().map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                  </svg>
                </div>
              </div>
            </div>

            {/* Sort Order */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sort Order
              </label>
              <select
                value={tempPreferences.year_sort.sort_order}
                onChange={(e) => handleYearChange('sort_order', e.target.value)}
                className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
                disabled={saving}
              >
                <option value="none">No sorting</option>
                <option value="ascending">Oldest first</option>
                <option value="descending">Newest first</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Current Settings Preview */}
      <div className="mt-6 p-4 bg-teal-50 rounded-lg border border-teal-200">
        <h4 className="font-medium text-teal-900 mb-2">Current Settings:</h4>
        <div className="text-sm text-teal-700 space-y-1">
          <div>
            <strong>Price:</strong> 
            {preferences.price_sort.min_value || preferences.price_sort.max_value || preferences.price_sort.sort_order !== 'none'
              ? ` ¥${preferences.price_sort.min_value || 'No min'} - ¥${preferences.price_sort.max_value || 'No max'}, ${preferences.price_sort.sort_order}`
              : ' No filters applied'
            }
          </div>
          <div>
            <strong>Year:</strong> 
            {preferences.year_sort.min_value || preferences.year_sort.max_value || preferences.year_sort.sort_order !== 'none'
              ? ` ${preferences.year_sort.min_value || 'No min'} - ${preferences.year_sort.max_value || 'No max'}, ${preferences.year_sort.sort_order}`
              : ' No filters applied'
            }
          </div>
        </div>
      </div>

      {/* Map Alert Service Section */}
      <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-600">
              To add or change the pins, visit your map now
            </p>
          </div>
          <Link to={"/mapwisenotification"}>
            <button className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors">
              Map Alert Service
            </button>
          </Link>
        </div>
      </div>

      {/* Bottom Save Button - Fixed Position for Mobile */}
      {hasChanges && (
        <div className="sticky bottom-4 mt-8 flex justify-center">
          <div className="bg-white  p-2">
            <SaveButton position="bottom" className="shadow-md" />
          </div>
        </div>
      )}
    </div>
  );
};

export default SortingPreferences;